import { sortByKey } from '@semios/app-platform-common'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { getAlternatingRowStyle, icsTableStyles } from '../_utils/icsTableStyles'
import { useIrrigationZoneNameDictionary } from '../_utils/useIrrigationZoneNameDictionary'
import { formatDifference } from './_utils/formatDifference'
import type { TPlannedVsAppliedTableProps } from './types'

const headerCellRight = {
  ...icsTableStyles.headerCell,
  fontWeight: 'bold',
  textAlign: 'right' as const,
  padding: '8px 18px',
  width: '20%',
}

const bodyCellRight = {
  ...icsTableStyles.bodyCell,
  textAlign: 'right' as const,
}

export const PlannedVsAppliedTable = ({
  title,
  zoneSummaries,
  includeApplied = true,
}: TPlannedVsAppliedTableProps) => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const irrigationZoneEmitters = selectedProperty?.irrigationZoneEmitters ?? {}
  const irrigationZoneNameDictionary = useIrrigationZoneNameDictionary(irrigationZoneEmitters)

  return (
    <div>
      <div css={{ textAlign: 'center' }}>{title}</div>
      <table
        css={{
          ...icsTableStyles.table,
          margin: '16px 0',
          fontSize: 14,
        }}
      >
        <thead>
          <tr>
            <th
              css={{
                ...icsTableStyles.headerCell,
                fontWeight: 'bold',
                textAlign: 'left',
                padding: '8px 18px',
                width: includeApplied ? '40%' : '80%',
              }}
            >
              {translate.phrases.banyanApp('Zone')}
            </th>
            <th css={headerCellRight}>{translate.phrases.banyanApp('Planned')}</th>
            {includeApplied && (
              <>
                <th css={headerCellRight}>{translate.phrases.banyanApp('Applied')}</th>
                <th css={headerCellRight}>{translate.phrases.banyanApp('Diff(%)')}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {zoneSummaries
            .map((zone) => {
              const zoneName = irrigationZoneNameDictionary[zone.zoneId]

              if (!zoneName) return null

              return { ...zone, zoneName }
            })
            .filter((zone) => zone !== null)
            .sort(sortByKey('zoneName'))
            .map((zone, index) => {
              const planned = zone.plannedMinutes
              const applied = zone.appliedMinutes
              const difference = planned > 0 ? ((applied - planned) / planned) * 100 : null

              let differenceColor = colors.grey900

              if (difference !== null && difference > 0) differenceColor = colors.green500
              else if (difference !== null && difference < 0) differenceColor = colors.strongRed

              return (
                <tr key={zone.zoneId} css={getAlternatingRowStyle(index)}>
                  <td
                    css={{
                      ...icsTableStyles.bodyCellLeft,
                      whiteSpace: 'normal',
                    }}
                  >
                    {zone.zoneName}
                  </td>
                  <td css={bodyCellRight}>{minutesToHoursAndMinutes(planned)}</td>
                  {includeApplied && (
                    <>
                      <td css={bodyCellRight}>{minutesToHoursAndMinutes(applied)}</td>
                      <td css={{ ...bodyCellRight, fontWeight: 'bold', color: differenceColor }}>
                        {formatDifference(difference)}
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
        </tbody>
      </table>
    </div>
  )
}
